import { useQuery } from '@tanstack/react-query';
import { formatDateTime, formatSignedQuantity, humanise } from '../../../lib/format';
import { recentMovementsQuery } from '../queries';
import {
  WidgetCard,
  WidgetEmpty,
  WidgetError,
  WidgetList,
  WidgetRow,
  WidgetSkeleton,
  RowTitle,
} from '../primitives';

/** The latest ledger entries — a quick sanity view of what has moved. */
export function RecentMovementsWidget({
  limit = 8,
  to,
  description,
}: {
  limit?: number;
  to?: string;
  description?: string;
}) {
  const query = useQuery(recentMovementsQuery(limit));

  return (
    <WidgetCard
      title="Recent movements"
      description={description ?? 'Every stock change is posted to the ledger as it happens.'}
      to={to}
      toLabel="Open ledger"
    >
      {query.isPending ? <WidgetSkeleton /> : null}
      {query.isError ? (
        <WidgetError label="Could not load movements." onRetry={() => void query.refetch()} />
      ) : null}
      {query.isSuccess && query.data.items.length === 0 ? (
        <WidgetEmpty>Nothing has moved yet.</WidgetEmpty>
      ) : null}
      {query.isSuccess && query.data.items.length > 0 ? (
        <WidgetList>
          {query.data.items.map((movement) => (
            <WidgetRow key={movement.id}>
              <RowTitle
                title={
                  <>
                    {humanise(movement.type)} ·{' '}
                    <span className="font-mono text-xs font-normal">{movement.itemCode}</span>
                  </>
                }
                subtitle={`${movement.locationCode} · ${movement.actorName ?? 'system'} · ${formatDateTime(movement.occurredAt)}`}
              />
              <p
                className={`shrink-0 whitespace-nowrap text-sm font-semibold ${
                  movement.direction === 'out' ? 'text-red-700' : 'text-brand-deep'
                }`}
              >
                {formatSignedQuantity(movement.quantity, movement.uom)}
              </p>
            </WidgetRow>
          ))}
        </WidgetList>
      ) : null}
    </WidgetCard>
  );
}
